import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { GridApi } from 'ag-grid-community';
import SVG from 'react-inlinesvg';
import restoreSvg from 'assets/icons/restore.svg';
import editIcon from 'assets/icons/edit.svg';
import { CONTENT_MANAGEMENT_PAGE_SIZES } from 'consts/pagination';
import { getValidContentManagementPageSize } from 'utils/pageSize';
import CreativeCount from 'components/pages/ContentManagement/CreativeCount';
import Button, { Color, Size } from 'lib/Button';
import PageSize from 'lib/PageSize';
import Session from 'modules/Session';
import { restoreCreatives } from 'modules/api/bulkEditCreatives';
import { cancelFunctions } from 'utils/cancelFunctions';
import { selectCreatives } from 'store/pages/contentManagement/creatives/reducer';
import Modal from 'lib/Modal';
import withLoader from 'components/hocs/withLoader';
import { CreativeType } from 'components/common/types';
import {
  checkResponseResultErrors,
  getCreativeMarketId,
} from 'components/pages/ContentManagement/EditCreatives/EditPanel/utils';
import { Creative, CreativeCountSummary } from 'components/common/types/Creative.types';
import { FeatureFlags } from 'components/common/types/Features.types';
import { useHasFeatureAccess } from 'customHooks/useHasFeatureAccess';
import EmailNotificationButton from '../EmailNotificationButton';
import { CreativesTable } from './CreativesTable';
import { FetchCreativesParams, FetchCreativesResponse } from './Creatives.types';

const pageSizeKey = 'content';
const DEFAULT_CREATIVE_COUNT: CreativeCountSummary = {
  staticCreatives: 0,
  motionCreatives: 0,
};

interface Props {
  filterData: {
    deleted: boolean;
  };
  isCreativesSelected: boolean;
  setSelectedCreatives: (creatives: Creative[]) => void;
  onFetchCreatives: (params: FetchCreativesParams) => Promise<FetchCreativesResponse>;
}

const Creatives: React.FC<Props> = ({ filterData, isCreativesSelected, setSelectedCreatives, onFetchCreatives }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const creativesTableRef = useRef<{ refreshTable: () => void }>(null);

  const [pageSize, setPageSize] = useState(
    getValidContentManagementPageSize(Session.getContentManagementPageSizeByKey(pageSizeKey)),
  );
  const [totalCreativesSelected, setTotalCreativesSelected] = useState(0);
  const [creativeCountSummary, setCreativeCountSummary] = useState<CreativeCountSummary>(DEFAULT_CREATIVE_COUNT);
  const [bulkSelectedCreatives, setBulkSelectedCreatives] = useState<Creative[]>([]);
  const [isRestoreModalOpen, setIsRestoreModalOpen] = useState(false);

  const handleBulkEdit = (): void => {
    navigate('/content-management/edit-creatives');

    dispatch(selectCreatives(bulkSelectedCreatives));
  };

  const resetCreativeCount = (): void => {
    setTotalCreativesSelected(0);
    setCreativeCountSummary(DEFAULT_CREATIVE_COUNT);
  };

  const onSelectionChanged = (event: { api: GridApi }): void => {
    const getSelectedRows: Creative[] = event.api?.getSelectedRows();
    if (!getSelectedRows) return;

    setBulkSelectedCreatives(getSelectedRows);
    setTotalCreativesSelected(getSelectedRows.length > 0 ? getSelectedRows.length : 0);
    setCreativeCountSummary({
      staticCreatives: getSelectedRows.filter((item) => [CreativeType.BANNER, CreativeType.DYNAMIC].includes(item.type))
        .length,
      motionCreatives: getSelectedRows.filter((item) => [CreativeType.VIDEO].includes(item.type)).length,
    });
    setSelectedCreatives(getSelectedRows);
  };

  const reApplyBulkSelection = (tableApi: GridApi | null): void => {
    if (bulkSelectedCreatives.length && tableApi) {
      tableApi.forEachNode((node) => {
        if (bulkSelectedCreatives.some((creative) => creative.id === node.data.id)) {
          node.setSelected(true, undefined, true);
        }
      });

      onSelectionChanged({ api: tableApi });
    }
  };

  const refreshCreativesTable = (): void => {
    setSelectedCreatives([]);
    resetCreativeCount();
    creativesTableRef.current?.refreshTable();
  };

  const handleBulkRestore = async (): Promise<void> => {
    try {
      checkResponseResultErrors(
        await restoreCreatives(cancelFunctions, {
          ids: bulkSelectedCreatives.map(getCreativeMarketId),
        }),
      );

      setBulkSelectedCreatives([]);
      refreshCreativesTable();
      // eslint-disable-next-line no-empty
    } catch {
    } finally {
      setIsRestoreModalOpen(false);
    }
  };

  useEffect(() => {
    Session.setContentManagementPageSizeByKey(pageSizeKey, pageSize);
  }, [pageSize]);

  useEffect(() => {
    setSelectedCreatives([]);
    resetCreativeCount();
  }, [filterData]);

  return (
    <>
      <div className="flex justify-between items-center mb-3">
        <PageSize
          dataTestId="page-size-selection"
          onPageSizeChange={setPageSize}
          pageSize={pageSize}
          pageInfoText="creatives per page"
          listOfPageSizes={CONTENT_MANAGEMENT_PAGE_SIZES}
        />
        <div className="flex items-center space-x-3">
          {!filterData.deleted && (
            <CreativeCount totalCreatives={totalCreativesSelected} creativeCountSummary={creativeCountSummary} />
          )}
          {useHasFeatureAccess(FeatureFlags.EMAIL_NOTIFICATION) && <EmailNotificationButton />}
          {!filterData.deleted && (
            <Button
              dataTestId="bulk-edit-button"
              onClick={handleBulkEdit}
              size={Size.LARGE}
              color={Color.PRIMARY}
              isDisabled={!isCreativesSelected}
            >
              <SVG className="fill-current text-neutral-50 w-3.5 h-3.5 mr-1" src={editIcon} />
              Bulk edit
            </Button>
          )}
          {filterData.deleted && isCreativesSelected && (
            <Button
              dataTestId="bulk-restore-button"
              size={Size.LARGE}
              color={Color.GREEN}
              onClick={() => setIsRestoreModalOpen(true)}
            >
              <SVG src={restoreSvg} className="w-3.5 h-3.5 mr-1" />
              Restore Creatives
            </Button>
          )}
        </div>
      </div>
      <CreativesTable
        ref={creativesTableRef}
        onFetchCreatives={onFetchCreatives}
        pageSize={pageSize}
        filters={filterData}
        reApplyBulkSelection={reApplyBulkSelection}
        onSelectionChanged={onSelectionChanged}
      />
      <Modal
        isOpen={isRestoreModalOpen}
        icon={restoreSvg}
        title="Restore Creatives?"
        actionButtons={
          <>
            <Button
              size={Size.LARGE}
              color={Color.SECONDARY}
              onClick={() => setIsRestoreModalOpen(false)}
              label="Cancel"
            />
            <Button size={Size.LARGE} color={Color.GREEN} onClick={handleBulkRestore} label="Restore" />
          </>
        }
      >
        <p className="ml-8 body-base text-neutral-950-opacity-60">Are you sure you want to restore these creatives?</p>
      </Modal>
    </>
  );
};

export default withLoader(Creatives);
